import React, { useMemo, useState, useId } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import UserSpaces from "../../molecules/Bands/UserSpaces";
import ThreeDotsButton from "../../atoms/ThreeDotsButton";
import WorkspaceHeaderPanel from "../../molecules/WorkspaceHeaderPanel";
import SidebarIcon from "../../atoms/icons/SidebarIcon";
import SidebarNavLink from "../../molecules/Sidebar/SidebarNavLink";
import SidebarSectionHeader from "../../molecules/Sidebar/SidebarSectionHeader";
import SidebarCollapsible from "../../molecules/Sidebar/SidebarCollapsible";
import ChannelListItem from "../../molecules/Sidebar/ChannelListItem";
import DirectMessageListItem from "../../molecules/Sidebar/DirectMessageListItem";
import BandsReturnToDiscoverButton from "../../molecules/Bands/BandsReturnToDiscoverButton";

const QUICK_ACCESS_LINKS = [
    { id: "unreads", icon: "unreads" },
    { id: "threads", icon: "threads" },
    { id: "drafts", icon: "drafts" },
    { id: "search", icon: "search" },
];

export default function BandSidebar({
    bands,
    selectedBandId = null,
    onSelectBand,
    onOpenBandCreator,
    channels,
    selectedChannelId = null,
    onSelectChannel,
    unreadByChannel = {},
    onOpenChannelCreator,
    privateChats = [],
    directMessagePreviews = {},
    onSelectDirectMessage,
    selectedDirectChatId = null,
    workspacePanel,
    onReturnToWelcome,
}) {
    const { t } = useTranslation();
    const [isChannelsOpen, setIsChannelsOpen] = useState(true);
    const [isDMOpen, setIsDMOpen] = useState(true);
    const [isWorkspaceInfoOpen, setIsWorkspaceInfoOpen] = useState(false);
    const baseSectionId = useId();
    const channelsPanelId = `${baseSectionId}-channels`;
    const directMessagesPanelId = `${baseSectionId}-direct-messages`;

    const unreadChannels = useMemo(() => channels.filter((channel) => Number(unreadByChannel?.[String(channel.id)]) > 0), [channels, unreadByChannel]);
    const otherChannels = useMemo(() => {
        const unreadIds = new Set(unreadChannels.map((channel) => String(channel.id)));
        return channels.filter((channel) => !unreadIds.has(String(channel.id)));
    }, [channels, unreadChannels]);

    const handleSelectChannel = (channelId) => {
        if (!channelId) {
            return;
        }
        onSelectChannel?.(channelId);
    };

    const quickAccessLinks = useMemo(() => QUICK_ACCESS_LINKS.map((link) => ({
        ...link,
        label: t(`bandsPage.sidebar.quickLinks.${link.id}`),
    })), [t]);

    return (
        <aside className="band-sidebar" aria-label={t("bandsPage.sidebar.aria")}>
            <div className="band-sidebar__workspace">
                <UserSpaces
                    bands={bands}
                    selectedBandId={selectedBandId}
                    onSelectBand={onSelectBand}
                    onCreateBand={onOpenBandCreator}
                />
                <ThreeDotsButton
                    className="user-spaces__kebab"
                    size={28}
                    aria-haspopup="true"
                    aria-expanded={isWorkspaceInfoOpen}
                    aria-label={t("bandsPage.sidebar.workspaceInfo")}
                    onClick={() => setIsWorkspaceInfoOpen((v) => !v)}
                />
            </div>
            {isWorkspaceInfoOpen && selectedBandId && workspacePanel && (
                <div className="band-sidebar__section">
                    <WorkspaceHeaderPanel
                        className="workspace-info-panel--sidebar"
                        {...workspacePanel}
                    />
                </div>
            )}
            <div className="band-sidebar__section">
                <ul className="band-sidebar__nav">
                    {quickAccessLinks.map((link) => (
                        <li key={link.id}>
                            <SidebarNavLink
                                icon={<SidebarIcon name={link.icon} />}
                                label={link.label}
                            />
                        </li>
                    ))}
                </ul>
            </div>
            {selectedBandId && (
            <div className="band-sidebar__section">
                <SidebarSectionHeader
                    title={t("bandsPage.sidebar.channels.title")}
                    isOpen={isChannelsOpen}
                    onToggle={() => setIsChannelsOpen((v) => !v)}
                    ariaLabel={t("bandsPage.sidebar.channels.aria")}
                    contentId={channelsPanelId}
                    action={(
                        <button
                            type="button"
                            className="band-sidebar__action"
                            onClick={onOpenChannelCreator}
                            disabled={!onOpenChannelCreator}
                            aria-label={t("bandsPage.sidebar.channels.create")}
                        >
                            ＋
                        </button>
                    )}
                />
                <SidebarCollapsible isOpen={isChannelsOpen} id={channelsPanelId}>
                    {isChannelsOpen && (
                        <ul className="band-sidebar__channels">
                            {unreadChannels.map((channel) => {
                                const channelKey = String(channel.id);
                                const unreadCount = Number(unreadByChannel?.[channelKey]) || 0;
                                const isActive = String(selectedChannelId) === channelKey;
                                return (
                                    <ChannelListItem
                                        key={`unread-${channel.id}`}
                                        channel={channel}
                                        unreadCount={unreadCount}
                                        isActive={isActive}
                                        onClick={() => handleSelectChannel(channel.id)}
                                        className="band-sidebar__channel--unread"
                                    />
                                );
                            })}
                            {otherChannels.map((channel) => {
                                const channelKey = String(channel.id);
                                const unreadCount = Number(unreadByChannel?.[channelKey]) || 0;
                                const isActive = String(selectedChannelId) === channelKey;
                                return (
                                    <ChannelListItem
                                        key={channel.id}
                                        channel={channel}
                                        unreadCount={unreadCount}
                                        isActive={isActive}
                                        onClick={() => handleSelectChannel(channel.id)}
                                    />
                                );
                            })}
                            {channels.length === 0 && (
                                <li className="band-sidebar__empty">{t("bandsPage.sidebar.channels.empty")}</li>
                            )}
                        </ul>
                    )}
                </SidebarCollapsible>
            </div>
            )}

            {/* Direct messages section */}
            <div className="band-sidebar__section">
                <SidebarSectionHeader
                    title={t("bandsPage.sidebar.direct.title")}
                    isOpen={isDMOpen}
                    onToggle={() => setIsDMOpen((v) => !v)}
                    ariaLabel={t("bandsPage.sidebar.direct.aria")}
                    contentId={directMessagesPanelId}
                />
                <SidebarCollapsible isOpen={isDMOpen} id={directMessagesPanelId}>
                    {isDMOpen && (
                        <ul className="band-sidebar__channels">
                            {privateChats.map((dm) => {
                                const dmKey = String(dm.chatId || dm.id);
                                const previewKey = dm.chatId !== undefined && dm.chatId !== null
                                    ? String(dm.chatId)
                                    : null;
                                const previewEntry = previewKey
                                    ? directMessagePreviews?.[previewKey]
                                    : undefined;
                                return (
                                    <DirectMessageListItem
                                        key={dmKey}
                                        dm={dm}
                                        isActive={String(selectedDirectChatId) === dmKey}
                                        preview={previewEntry?.text}
                                        isTyping={Boolean(previewEntry?.typing)}
                                        timestamp={previewEntry?.updatedAt}
                                        onClick={() => onSelectDirectMessage?.(dm)}
                                    />
                                );
                            })}
                        </ul>
                    )}
                </SidebarCollapsible>
            </div>
            <div className="band-sidebar__footer">
                <BandsReturnToDiscoverButton onClick={onReturnToWelcome} />
            </div>

        </aside>
    );
}

BandSidebar.propTypes = {
    bands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        iconUrl: PropTypes.string,
    })).isRequired,
    selectedBandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectBand: PropTypes.func.isRequired,
    onOpenBandCreator: PropTypes.func,
    channels: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string,
        boardId: PropTypes.string,
        privateChannel: PropTypes.bool,
    })).isRequired,
    selectedChannelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectChannel: PropTypes.func.isRequired,
    unreadByChannel: PropTypes.object,
    onOpenChannelCreator: PropTypes.func,
    directMessagePreviews: PropTypes.objectOf(PropTypes.shape({
        text: PropTypes.string,
        typing: PropTypes.bool,
        hydrated: PropTypes.bool,
        updatedAt: PropTypes.number,
    })),
    privateChats: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        chatId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        friendUserId: PropTypes.string.isRequired,
        username: PropTypes.string,
        online: PropTypes.bool,
        lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        presenceStatus: PropTypes.oneOf(["online", "offline", "away", "unknown"]),
        isPersonal: PropTypes.bool,
        needsCreation: PropTypes.bool,
    })),
    onSelectDirectMessage: PropTypes.func,
    selectedDirectChatId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workspacePanel: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        iconUrl: PropTypes.string,
        imageAlt: PropTypes.string,
        imageEditLabel: PropTypes.string,
        imageUpdatingLabel: PropTypes.string,
        memberCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        visibility: PropTypes.string,
        role: PropTypes.string,
        canManageMembers: PropTypes.bool,
        canEditImage: PropTypes.bool,
        isImageUpdating: PropTypes.bool,
        isLeaving: PropTypes.bool,
        isDeleting: PropTypes.bool,
        isVisibilityUpdating: PropTypes.bool,
        onEditImage: PropTypes.func,
        onLeaveWorkspace: PropTypes.func,
        onRequestDeleteWorkspace: PropTypes.func,
        onToggleVisibility: PropTypes.func,
        onOpenInfo: PropTypes.func,
    }),
    onReturnToWelcome: PropTypes.func,
};
